import React, { useState, useEffect } from "react";
import Header from "./components/Header";
import ReadingRoomHome from "./components/ReadingRoomHome";
import ArticlePage from "./components/ArticlePage";
import { BlogPost } from "./types/blog";
import {
  getAllPosts,
  getPostById,
  updateLikes,
  addComment,
  deleteComment,
} from "./data/supabasePosts";
import About from "./components/About";
import Footer from "./components/Footer";
import AdminPanel from "./components/AdminPanel";
import { AuthProvider } from "./context/AuthContext";
import AdminRoute from "./components/AdminRoute";
import { useAuth } from "./context/AuthContext";
import ErrorBoundary from "./components/ErrorBoundary";
import LoadingSpinner from "./components/LoadingSpinner";
import PasswordReset from "./components/PasswordReset";

// Create a wrapper component that uses the auth context
const AppContent: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [showAbout, setShowAbout] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [postLoading, setPostLoading] = useState(false);
  const { user, signOut } = useAuth();

  // Check URL for routing
  useEffect(() => {
    const path = window.location.pathname;

    // Check for admin path
    if (path === "/secret-admin-path") {
      setShowAdmin(true);
      return;
    }

    // Check for password reset path
    if (path === "/reset-password") {
      setShowPasswordReset(true);
      return;
    }

    // Check for about page
    if (path === "/about") {
      setShowAbout(true);
      return;
    }

    // Check for post path
    const postMatch = path.match(/^\/post\/(.+)$/);
    if (postMatch && postMatch[1]) {
      const postId = postMatch[1];

      // Load the specific post
      const loadPost = async () => {
        try {
          setPostLoading(true);
          setError(null);
          const post = await getPostById(postId);
          if (post) {
            setSelectedPost(post);
          } else {
            setError("Post not found");
            // Post not found, redirect to home after a delay
            setTimeout(() => {
              window.history.pushState({}, "", "/");
              setSelectedPost(null);
              setShowAbout(false);
              setShowAdmin(false);
              setShowPasswordReset(false);
              setError(null);
            }, 2000);
          }
        } catch (err) {
          console.error("Error loading post:", err);
          setError("Failed to load post");
        } finally {
          setPostLoading(false);
        }
      };

      loadPost();
    }
  }, []);

  useEffect(() => {
    const loadPosts = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const allPosts = await getAllPosts();
        setPosts(allPosts);
      } catch (err) {
        console.error("Error loading posts:", err);
        setError("Failed to load blog posts");
      } finally {
        setIsLoading(false);
      }
    };
    loadPosts();
  }, []);

  const handleLike = async (postId: string) => {
    try {
      // Find the post and increment its likes
      const post = posts.find((p) => p.id === postId);
      if (post) {
        const newLikes = (post.likes || 0) + 1;
        const success = await updateLikes(postId, newLikes);

        if (success) {
          // Update local state
          setPosts(
            posts.map((p) => (p.id === postId ? { ...p, likes: newLikes } : p))
          );

          // Update selected post if it's the one being liked
          if (selectedPost && selectedPost.id === postId) {
            setSelectedPost({ ...selectedPost, likes: newLikes });
          }
        } else {
          throw new Error("Failed to update likes");
        }
      }
    } catch (err) {
      console.error("Error updating likes:", err);
    }
  };

  const handleAddComment = async (postId: string, content: string) => {
    try {
      const comment = {
        author: user?.email || "Anonymous", // Use user email if available
        content,
        date: new Date().toISOString(),
      };

      const newComment = await addComment(postId, comment);

      if (newComment) {
        // Update local state
        const updatedPosts = posts.map((post) => {
          if (post.id === postId) {
            const updatedComments = [...(post.comments || []), newComment];
            return { ...post, comments: updatedComments };
          }
          return post;
        });

        setPosts(updatedPosts);

        // Update selected post if it's the one being commented on
        if (selectedPost && selectedPost.id === postId) {
          const updatedComments = [...(selectedPost.comments || []), newComment];
          setSelectedPost({ ...selectedPost, comments: updatedComments });
        }
      } else {
        throw new Error("Failed to add comment");
      }
    } catch (err) {
      console.error("Error adding comment:", err);
      throw err; // Re-throw to let the component handle the error
    }
  };

  const handleDeleteComment = async (id: string) => {
    try {
      const success = await deleteComment(id);

      if (success && selectedPost) {
        // Update local state
        const updatedPosts = posts.map((post) => {
          if (post.id === selectedPost.id) {
            const updatedComments = (post.comments || []).filter(
              (comment) => comment.id !== id
            );
            return { ...post, comments: updatedComments };
          }
          return post;
        });

        setPosts(updatedPosts);

        // Update selected post
        const updatedComments = (selectedPost.comments || []).filter(
          (comment) => comment.id !== id
        );
        setSelectedPost({ ...selectedPost, comments: updatedComments });
      } else {
        throw new Error("Failed to delete comment");
      }
    } catch (err) {
      console.error("Error deleting comment:", err);
      throw err; // Re-throw to let the component handle the error
    }
  };

  const handleHomeClick = () => {
    setSelectedPost(null);
    setShowAbout(false);
    setShowAdmin(false);
    setShowPasswordReset(false);

    // Update URL
    window.history.pushState({}, "", "/");
  };

  const handleAboutClick = () => {
    setSelectedPost(null);
    setShowAbout(true);
    setShowAdmin(false);
    setShowPasswordReset(false);

    // Update URL
    window.history.pushState({}, "", "/about");
  };

  const handleAdminClick = () => {
    setSelectedPost(null);
    setShowAbout(false);
    setShowAdmin(true);
    setShowPasswordReset(false);

    // Optionally update URL without reloading the page
    window.history.pushState({}, "", "/secret-admin-path");
  };

  // Add a simple admin link in the header
  const headerProps = {
    onHomeClick: handleHomeClick,
    onAboutClick: handleAboutClick,
    onAdminClick: handleAdminClick,
    onSignOut: user ? signOut : undefined,
    isLoggedIn: !!user,
  };

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen flex flex-col transition-colors">
        <Header {...headerProps} />
        <main className="flex-1 w-full max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8 mt-2 sm:mt-4 mb-20">
          <div className="text-center py-12 animate-fade-in">
            <div className="mb-4">
              <svg
                className="w-16 h-16 text-red-500 dark:text-red-400 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-4">{error}</h2>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 transition-smooth"
            >
              Try Again
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (showPasswordReset) {
    return (
      <div className="min-h-screen flex flex-col transition-colors">
        <Header {...headerProps} />
        <main className="flex-1 w-full max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8 mt-2 sm:mt-4 mb-20">
          <ErrorBoundary>
            <PasswordReset />
          </ErrorBoundary>
        </main>
        <Footer />
      </div>
    );
  }

  if (showAdmin) {
    return (
      <div className="min-h-screen flex flex-col transition-colors">
        <Header {...headerProps} />
        <main className="flex-1 w-full max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8 mt-2 sm:mt-4 mb-20">
          <ErrorBoundary>
            <AdminRoute>
              <AdminPanel />
            </AdminRoute>
          </ErrorBoundary>
        </main>
        <Footer />
      </div>
    );
  }

  if (postLoading) {
    return (
      <div className="min-h-screen flex flex-col transition-colors">
        <Header {...headerProps} />
        <main className="flex-1 w-full max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8 mt-2 sm:mt-4 mb-20">
          <LoadingSpinner message="Loading post..." />
        </main>
        <Footer />
      </div>
    );
  }

  if (selectedPost) {
    return (
      <div className="min-h-screen flex flex-col transition-colors">
        <Header {...headerProps} />
        <main className="flex-1 w-full max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8 mt-2 sm:mt-4 mb-20">
          <ErrorBoundary>
            <ArticlePage
              post={selectedPost}
              onLike={() => handleLike(selectedPost.id)}
              onAddComment={(content) =>
                handleAddComment(selectedPost.id, content)
              }
              onDeleteComment={handleDeleteComment}
            />
          </ErrorBoundary>
        </main>
        <Footer />
      </div>
    );
  }

  if (showAbout) {
    return (
      <div className="min-h-screen flex flex-col transition-colors">
        <Header {...headerProps} />
        <main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 md:py-10 mb-20">
          <ErrorBoundary>
            <About />
          </ErrorBoundary>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <ReadingRoomHome
        posts={posts}
        isLoading={isLoading}
        isLoggedIn={Boolean(user)}
        onSignOut={signOut}
      />
    </ErrorBoundary>
  );
};

// Main App component that provides the auth context
function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
